import React, { JSX, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router';
import useAuth from 'src/hooks/useAuth';
import type { Certification } from 'src/types/Certification';
import type { WorkItemType, NavKind } from 'src/redux/slices/Interview/types';

import LoadingScreen from './screens/LoadingScreen';
import QuestionScreen from './screens/QuestionScreen';
import SignatureScreen from './screens/SignatureScreen';
import StaleDocScreen from './screens/StaleDocScreen';
import UserProfileFieldScreen from './screens/UserProfileFieldScreen';
import SkipGateScreen from './screens/SkipGateScreen';
import NothingToDoScreen from './screens/NothingToDoScreen';
import AddAnotherScreen from './screens/AddAnotherScreen';
import ErrorScreen from './screens/ErrorScreen';
import { RootState } from 'src/redux/rootReducerType';

/**
 * Thin redirect to the standalone AIFileUploadV2 routes. The IPE dispatches
 * `file_upload` whenever `pending_file_qits` is non-empty and the user has
 * not session-deferred the work item; the renter is bounced to the existing
 * /renter/files surface and lands back on /renter/interview/{surveyId} when
 * they finish or skip there.
 *
 * Routes to the cert-scoped path (/renter/certification/:certId/files/:userId)
 * so AIFileUploadV2Context resolves the cert-scoped survey via
 * /api/v2/renter-hub/survey/resolve/?certification_id=...&user_id=... rather
 * than falling into the profile-survey (Pronto Questionnaire) branch which
 * would 503 for renters whose tenants don't materialize a profile survey.
 *
 * Subject identity:
 *   - Self mode: subject is the logged-in user; redirect uses `user.id`.
 *   - Proxy mode: `state.interview.onBehalfOf` carries a User.id (set by
 *     InterviewRoute from the /members/:memberId URL param — User.id is
 *     the canonical "act on behalf of" identifier across all renter routes;
 *     the backend permission classes resolve it to the right authority
 *     domain — UserHouseholdMember for DV/Account Hub vs CertMember for
 *     cert-scoped flows).
 */
function FileUploadRedirect(): JSX.Element {
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const { user } = useAuth();
  const certId = useSelector(
    (state: { interview: { certId: number | null } }) => state.interview.certId
  );
  const surveyId = useSelector(
    (state: { interview: { surveyId: number | null } }) =>
      state.interview.surveyId
  );
  const onBehalfOf = useSelector(
    (state: { interview: { onBehalfOf: number | null } }) =>
      state.interview.onBehalfOf
  );
  const certifications = useSelector(
    (state: { newRenterInterface?: { certifications?: Certification[] } }) =>
      state.newRenterInterface?.certifications
  );

  const subjectUserId = useMemo<number | null>(() => {
    if (onBehalfOf == null) {
      return user?.id ?? null;
    }
    // Proxy mode: onBehalfOf is already a User.id under the v2 routing
    // contract. Defensive check: confirm the user is actually a member of
    // the active cert's household before navigating to their files context
    // (don't open the wrong member's upload session if Redux state is
    // stale).
    const cert = (certifications ?? []).find((c) => c.id === certId);
    const isMemberOfCert = cert?.cert_household?.cert_members?.some(
      (m) => m.user_id === onBehalfOf
    );
    return isMemberOfCert ? onBehalfOf : null;
  }, [onBehalfOf, user?.id, certifications, certId]);

  useEffect(() => {
    if (certId == null) {
      // Profile survey (Data Vault) context: navigate to the non-cert file hub.
      // Pass the current interview URL as returnPath so the upload flow can
      // navigate back here after skip or finish.
      // In proxy mode, include onBehalfOf in state so AIFileUploadV2Layout can
      // resolve the subject's name for the OnBehalfOfBanner. The backend already
      // verified proxy authority when the IPE call succeeded, so no cert check
      // is needed here.
      navigate('/renter/files', {
        state: {
          returnPath: `${pathname}${search}`,
          subjectUserId: onBehalfOf ?? undefined,
        },
      });
      return;
    }
    if (subjectUserId == null) {
      // Unresolvable proxy member — do NOT navigate (confidentiality risk).
      return;
    }
    const interviewReturnPath = `${pathname}${search}`;
    navigate(`/renter/certification/${certId}/files/${subjectUserId}`, {
      state: {
        returnPath: interviewReturnPath,
        subjectUserId: onBehalfOf ?? undefined,
      },
    });
  }, [navigate, certId, subjectUserId, pathname, search, surveyId]); // eslint-disable-line react-hooks/exhaustive-deps

  return <LoadingScreen />;
}

const SCREEN_MAP: Record<WorkItemType, React.ComponentType> = {
  loading: LoadingScreen,
  question: QuestionScreen,
  signature: SignatureScreen,
  stale_document: StaleDocScreen,
  file_upload: FileUploadRedirect,
  user_profile_field: UserProfileFieldScreen,
  skip_gate: SkipGateScreen,
  complete: LoadingScreen, // never rendered — redirect in useEffect above
  nothing_to_do: NothingToDoScreen,
  add_another: AddAnotherScreen,
  error: ErrorScreen,
};

const REVIEW_KIND_MAP: Record<NavKind, React.ComponentType> = {
  question: QuestionScreen,
  user_profile_field: UserProfileFieldScreen,
};

export default function WorkItemRenderer(): JSX.Element {
  const workItemType = useSelector(
    (state: RootState) => state.interview.workItemType
  );
  const history = useSelector((state: RootState) => state.interview.history);
  const cursor = useSelector((state: RootState) => state.interview.cursor);
  const surveyId = useSelector((state: RootState) => state.interview.surveyId);
  const navigate = useNavigate();
  const location = useLocation();

  const isReviewing = history.length > 0 && cursor < history.length;

  useEffect(() => {
    if (
      workItemType === 'complete' &&
      surveyId != null &&
      !(location.state as { fromComplete?: boolean })?.fromComplete
    ) {
      navigate(`/renter/interview/${surveyId}/complete`, { replace: true });
    }
  }, [workItemType, surveyId, navigate, location.state]);

  if (isReviewing) {
    const step = history[cursor];
    const ReviewScreen = REVIEW_KIND_MAP[step.kind] ?? ErrorScreen;
    return <ReviewScreen />;
  }

  // During the redirect, don't render anything stale for 'complete'
  if (workItemType === 'complete') {
    return <LoadingScreen />;
  }

  const Screen = SCREEN_MAP[workItemType] ?? ErrorScreen;
  return <Screen />;
}
